import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import {
  obligacionesRecurrentesService,
  type ObligacionRecurrenteUpsertInput,
} from '@/services/obligacionesRecurrentesService'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import { toast } from 'sonner'

type FilaGeneracion = { periodos_procesados: number; vencimientos_creados: number }

/**
 * ORR-223: traduce el resultado de `generar_vencimientos_obligacion` a un mensaje accionable.
 *
 * El toast anterior decía "verificá los filtros" ante cualquier 0, y por eso el reporte de
 * Recategorización llegó con capturas del panel de filtros — que estaba bien. La causa real
 * era que la periodicidad no producía ningún período dentro de la ventana.
 * `periodos_procesados` separa los dos casos: 0 períodos = problema de periodicidad/fechas;
 * períodos > 0 con 0 creados = problema de alcance (filtros) o ya estaban cargados.
 */
export const resultadoGeneracion = (data: FilaGeneracion[] | null) => {
  const filas = data ?? []
  const generados = filas.reduce((acc, r) => acc + (r.vencimientos_creados ?? 0), 0)
  const periodos = filas.reduce((acc, r) => acc + (r.periodos_procesados ?? 0), 0)

  if (generados > 0) {
    return {
      tono: 'success' as const,
      mensaje: `${generados} ${generados === 1 ? 'vencimiento generado' : 'vencimientos generados'}.`,
    }
  }
  if (periodos === 0) {
    return {
      tono: 'warning' as const,
      mensaje:
        'No cae ningún vencimiento en los próximos 12 meses con esta periodicidad. Revisá periodicidad, día de vencimiento y meses de offset.',
    }
  }
  return {
    tono: 'info' as const,
    mensaje: `Sin vencimientos nuevos: se evaluaron ${periodos} ${periodos === 1 ? 'período' : 'períodos'} y los clientes alcanzados ya los tenían cargados.`,
  }
}

export const obligacionesRecurrentesKeys = {
  all: ['obligaciones-recurrentes'] as const,
  lists: () => [...obligacionesRecurrentesKeys.all, 'list'] as const,
  list: (organizacionId?: string | null) => [...obligacionesRecurrentesKeys.lists(), organizacionId] as const,
}

export function useObligacionesRecurrentes() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: obligacionesRecurrentesKeys.list(organizacionId),
    queryFn: () => obligacionesRecurrentesService.listar(supabase!, organizacionId!),
    enabled: !!supabase && !!organizacionId,
  })
}

export function useCrearObligacionRecurrente() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ObligacionRecurrenteUpsertInput) => {
      assertPermiso(puede, 'editarVencimiento')
      return obligacionesRecurrentesService.guardar(supabase!, {
        id: null,
        organizacionId: organizacionId!,
        input: data,
      })
    },
    onSuccess: async (createdId) => {
      queryClient.invalidateQueries({ queryKey: obligacionesRecurrentesKeys.lists() })
      // ORR-198 A3: generar vencimientos y reportar cuántos — antes era silencioso.
      // ORR-223: sin `p_meses_adelante`; la ventana la define el default de la función
      // (12 meses), que es también la que usa el cron de regeneración mensual.
      const { data, error } = await supabase!.rpc('generar_vencimientos_obligacion', {
        p_obligacion_id: createdId,
      })
      queryClient.invalidateQueries({ queryKey: ['calendario'] })
      if (error) {
        console.error('Error auto-generando vencimientos:', error)
        toast.warning('Obligación creada, pero no se pudieron generar los vencimientos. Revisá los datos e intentá reactivarla.')
        return
      }
      const { tono, mensaje } = resultadoGeneracion(data)
      toast[tono](`Obligación creada. ${mensaje}`)
    },
    onError: (error) => {
      console.error('Error creando obligación:', error)
      toast.error('Error al crear obligación')
    },
  })
}

export function useActualizarObligacionRecurrente() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ObligacionRecurrenteUpsertInput }) => {
      assertPermiso(puede, 'editarVencimiento')
      return obligacionesRecurrentesService.guardar(supabase!, {
        id,
        organizacionId: organizacionId!,
        input: data,
      })
    },
    onSuccess: async (updatedId) => {
      queryClient.invalidateQueries({ queryKey: obligacionesRecurrentesKeys.lists() })
      // ORR-198 A3: regenerar vencimientos (limpia stale de cambios de filtro) y reportar cuántos
      const { data, error } = await supabase!.rpc('generar_vencimientos_obligacion', {
        p_obligacion_id: updatedId,
      })
      queryClient.invalidateQueries({ queryKey: ['calendario'] })
      if (error) {
        console.error('Error auto-generando vencimientos:', error)
        toast.warning('Obligación actualizada, pero no se pudieron regenerar los vencimientos.')
        return
      }
      const { tono, mensaje } = resultadoGeneracion(data)
      toast[tono](`Obligación actualizada. ${mensaje}`)
    },
    onError: (error) => {
      console.error('Error actualizando obligación:', error)
      toast.error('Error al actualizar obligación')
    },
  })
}

export function useEliminarObligacionRecurrente() {
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, borrarVencimientosPendientes = true }: { id: string; borrarVencimientosPendientes?: boolean }) => {
      assertPermiso(puede, 'editarVencimiento')
      return obligacionesRecurrentesService.eliminar(supabase!, id, borrarVencimientosPendientes)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: obligacionesRecurrentesKeys.lists() })
      // Invalidar también vencimientos en caso de que el cascade haya eliminado registros
      queryClient.invalidateQueries({ queryKey: ['calendario'] })
      toast.success('Obligación eliminada exitosamente')
    },
    onError: (error) => {
      console.error('Error eliminando obligación:', error)
      toast.error('Error al eliminar obligación')
    },
  })
}

export function useToggleObligacionRecurrente() {
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) => {
      assertPermiso(puede, 'editarVencimiento')
      return obligacionesRecurrentesService.toggleEstado(supabase!, id, activo)
    },
    onSuccess: (_, { activo }) => {
      queryClient.invalidateQueries({ queryKey: obligacionesRecurrentesKeys.lists() })
      toast.success(activo ? 'Obligación activada' : 'Obligación desactivada')
    },
    onError: (error) => {
      console.error('Error cambiando estado:', error)
      toast.error('Error al cambiar estado')
    },
  })
}
